/**
 * Container implementation creates and wires together all application dependencies.
 * It instantiates each dep with its required dependencies, creating a dependency graph
 * where lower-level deps (like gameRegistry) are created first and passed
 * to higher-level deps (like roomLink, bot). The container handles mode detection
 * (local vs worker) and creates the appropriate implementations. After creation, it
 * initializes roomLink which sets up all event listeners and effects.
 */
import { effect } from '@preact/signals-react'
import type { WebContainer } from '../containers.types'
import { makeRoomLink } from '../room/roomLink/roomLink'
import { makeGameRegistry } from '../catalog/gameRegistry/gameRegistry'
import { makeGameCatalogDep } from '../catalog/gameCatalog'
import type { GameCatalogClient } from '@merels/room'
import { makeBot } from '../bot/bot/bot'
import { makeMoveHistory } from '../game/moveHistory/moveHistory'
import { makeAiTurnRunner } from '../bot/aiTurnRunner/aiTurnRunner'
import { makeDebugPanelVM } from '../ui/debugPanelVM/debugPanelVM'
import { makeMoveHistoryUI } from '../ui/debugPanelVM/moveHistoryUI/moveHistoryUI'
import { makeStateEditor } from '../debug/stateEditor/stateEditor'
import { makeMode } from '../mode/mode/mode'
import { makeInput } from '../input/input/input'
import { makeMatesToPlayersDep } from '../matesToPlayers'
import { makeLocalMates } from '../localMates/localMates'
import { makeGameOptionsDialogDep } from '../ui/playAreaVM/gameOptionsDialog/gameOptionsDialog'
import { makeLobby } from '../lobby/lobby'
import { makeGamepadEmulation } from '../debug/gamepadEmulation/gamepadEmulation'
import { makeShellVMDep } from '../ui/shellVM'
import { makePlayAreaVMDep } from '../ui/playAreaVM'
import { makeGameCurationVMDep } from '../ui/gameCurationVM'
import { makeMateListVMDep } from '../ui/mateListVM'
import { makeTurnIndicatorVMDep } from '../ui/turnIndicatorVM'
import { makeVideo } from '../video/video/video'
import { getOrGenerateClientId } from '../utils/clientId'

import { makeRoomDep } from '../room/room'
import { setupRoomInitialization } from '../room/init'
import { makeAuthDep } from '../auth/auth'
import { makeCrashReporter } from '../debug/crashReporter/crashReporter'

function getAuthDisplayName(user: any): string {
  return user ? (user.fullName || user.username || user.firstName || '').trim() : ''
}

export function makeWebContainer(
  baseUrl: string,
): WebContainer {

  const gamepadEmulation = makeGamepadEmulation()

  // Input
  const input = makeInput({
    controllerInputDeps: {
      keyboardEmulation: gamepadEmulation.keyboardWriter,
    },
  })

  // Start controller input listeners in the browser.
  // Without this, keyboard/gamepad semantic events (including `togglePlayer` / backtick)
  // will never be emitted.
  if (typeof window !== 'undefined') {
    input.controllerInput.start()
  }

  // Mode
  const mode = makeMode({ controllerInput: input.controllerInput })

  // Auto-enable gamepad emulation in shared screen mode for keyboard-to-controller routing
  if (mode.isSharedScreenMode() && !gamepadEmulation.isEnabled.value) {
    gamepadEmulation.enable()
    gamepadEmulation.connect('X1')
    gamepadEmulation.connect('X2')
    // Select X1 by default so keyboard routes to it
    gamepadEmulation.selectedControllerId.value = 'X1'
  }
  
  // Auth must exist before the catalog so its HTTP snapshot can include the
  // signed-in creator's preview-only games.
  const auth = makeAuthDep()

  // Catalog
  const gameRegistry = makeGameRegistry(baseUrl, undefined, () => auth.getToken())
  const gameCatalog = makeGameCatalogDep(gameRegistry)

  const refreshCatalog = (): void => {
    void gameRegistry.refreshCatalog?.().catch((error) => {
      console.error('[Catalog] Failed to refresh visible games:', error)
    })
  }

  effect(() => {
    void auth.currentUser.value
    if (!auth.isLoaded.value) return
    refreshCatalog()
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => {
      if (auth.currentUser.value) refreshCatalog()
    })
  }

  // Resolves a game's published version at start (see @merels/room's
  // GameCatalogClient) — distinct from the `gameCatalog` dep above, which is
  // the game-selection-screen catalog.
  const gameVersionCatalog: GameCatalogClient = {
    resolveVersion: (gameId, channel) => gameRegistry.resolveGameVersion?.(gameId, channel) ?? Promise.resolve(null),
  }

  // Lobby
  const lobby = makeLobby()

  // Game
  const moveHistory = makeMoveHistory()

  // Room
  const roomDep = makeRoomDep({ gameRegistry, gameCatalog: gameVersionCatalog, moveHistory, auth }, { workerUrl: baseUrl })
  const { state: roomStatePublic, actions: roomActions, connection: roomConnection } = roomDep
  const roomProxyManager = (roomDep as any)._internal.roomProxyManager

  // Merge state and connection to satisfy RoomStateInternal consumers
  const roomState = {
    ...roomStatePublic,
    status: roomConnection.status,
    roomId: roomConnection.roomId,
    error: roomConnection.error,
    pendingLocalMates: null,
  } as any


  // Initialize loading state from lobby
  roomState.isLoading.value = lobby.initialIsLoadingRoomState

  // LocalMates system (mates that originate from this client).
  const localMatesSystem = makeLocalMates({ mode, connectedControllers: input.connectedControllers })

  effect(() => {
    const isSharedScreen = mode.computedMode.value.sharedScreen
    if (isSharedScreen || mode.isOmniMode()) return
    localMatesSystem.localMates.setAuthenticatedName(getAuthDisplayName(auth.currentUser.value))
  })

  // Default the local mate's thumbnail from the signed-in user's Clerk profile photo,
  // once, the first time both are available. One-shot (not a continuous sync) so it
  // never overwrites a thumbnail the mate explicitly chose or cleared afterwards.
  // Skipped in shared-screen mode: the first controller-based mate there is whoever's
  // holding that controller, not necessarily the signed-in browser owner - defaulting
  // it from *this browser's* Clerk session would put the wrong person's photo on it.
  let appliedClerkThumbnailDefault = false
  effect(() => {
    const isSharedScreen = mode.computedMode.value.sharedScreen
    if (appliedClerkThumbnailDefault || isSharedScreen) return
    const imageUrl = auth.currentUser.value?.imageUrl
    if (!imageUrl) return
    const mateId = localMatesSystem.localMates.localMates.value[0]?.mateId
    if (!mateId) return
    appliedClerkThumbnailDefault = true
    localMatesSystem.localMates.updateMateThumbnail(mateId, imageUrl)
  })

  // MatesToPlayers (player assignment preferences for shared-screen mode).
  const matesToPlayers = makeMatesToPlayersDep()

  // Bot
  const bot = makeBot({ gameRegistry })

  // Room
  const roomLink = makeRoomLink(baseUrl)



  // AI turn runner sets up effects internally - no return value needed
  makeAiTurnRunner({
    bot,
    roomState,
    roomProxyManager,
    onError: (error) => { roomState.error.value = error },
  })

  // Initialize room systems (URL watcher, keyboard shortcuts, auto-join)
  setupRoomInitialization({
    roomState,
    lobby,
    activeOmniMate: localMatesSystem.omniMates.activeOmniMate,
    localMates: localMatesSystem.localMates,
    mode,
    auth,
    roomActions,
    roomConnection,
    workerUrl: baseUrl,
  })

  // UI
  const debugPanelVM = makeDebugPanelVM({
    roomState,
    roomConnection,
    roomActions,
    gameRegistry,
    bot,
    moveHistory,
    activeOmniMate: localMatesSystem.omniMates.activeOmniMate,
    localMates: localMatesSystem.localMates,
    controllerMates: localMatesSystem.controllerMates,
    matesToPlayers,
    mode,
    gamepadEmulation,
    auth,
    gameCatalog,
  })
  const moveHistoryUI = makeMoveHistoryUI()
  const stateEditor = makeStateEditor()
  const gameOptionsDialog = makeGameOptionsDialogDep(roomState, gameRegistry)
  const shellVM = makeShellVMDep({
    roomState: roomStatePublic,
    roomConnection,
    roomActions,
    gameRegistry,
    localMates: localMatesSystem.localMates,
    roomName: roomStatePublic.roomName,
    debugPanelVM,
    moveHistory,
    mode,
    focusNavigation: mode.focusNavigation,
    controllerInput: input.controllerInput,
    gamepadEmulation,
    auth,
  })
  const gameCuration = makeGameCurationVMDep({
    roomState: roomStatePublic,
    roomActions: roomActions,
    gameCatalog,
    isOwner: shellVM.room.isOwner,
  })
  const playAreaVM = makePlayAreaVMDep({
    roomState: roomStatePublic,
    roomConnection,
    roomActions,
    localMatesSystem,
    mode,
    focusNavigation: mode.focusNavigation,
    controllerInput: input.controllerInput,
    controllerMates: localMatesSystem.controllerMates,
    playerAssignmentPreferences: matesToPlayers.playerAssignmentPreferences,
    gameOptionsDialog,
    debugPanelVM,
    gameRegistry,
    gameCatalog,
    gameCuration,
    bot,
    auth,
  })

  const clientId = getOrGenerateClientId()

  // Video (Daily.co). Only real for networked, non-test rooms - see ../video/AGENTS.md.
  const video = makeVideo({
    roomState,
    mode,
    clientId,
    workerUrl: baseUrl,
  })

  const mateListVM = makeMateListVMDep({
    activeOmniMate: localMatesSystem.omniMates.activeOmniMate,
    roomState,
    gameRegistry,
    controllerMates: localMatesSystem.controllerMates,
    mode,
    video,
    clientId,
    localMates: localMatesSystem.localMates,
    bot,
    isPlatformDeveloper: debugPanelVM.isPlatformDeveloper,
    isOwner: shellVM.room.isOwner,
    setHost: roomActions.setHost,
  })

  const turnIndicatorVM = makeTurnIndicatorVMDep({
    mateListVM,
    roomState,
  })

  return {
    gameRegistry,
    gameCatalog,
    roomState,
    roomActions,
    roomConnection,
    bot,
    moveHistory,
    debugPanelVM,
    moveHistoryUI,
    stateEditor,
    matesToPlayers,
    localMatesSystem,
    focusNavigation: mode.focusNavigation,
    controllerInput: input.controllerInput,
    mode,
    connectedControllers: input.connectedControllers,
    input,
    gamepadEmulation,
    shellVM,
    playAreaVM,
    mateListVM,
    turnIndicatorVM,
    lobby,
    gameOptionsDialog,
    roomLink,
    video,
    auth,
    crashReporter: makeCrashReporter(baseUrl),
  }
}
